import { useState, useRef } from 'react';
import {
  X, UploadSimple, Spinner, CheckCircle, XCircle, Warning, FileArrowUp,
  ArrowClockwise, CloudArrowUp, Check,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP = { UPLOAD: 'upload', PREVIEW: 'preview', RESULT: 'result' };

export const TarefasImportarModal = ({ onClose, onImported }) => {
  const [step, setStep] = useState(STEP.UPLOAD);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(STEP.UPLOAD);
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    if (result && result.imported > 0) onImported?.();
    onClose();
  };

  const handleFileChoose = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) { setError('Selecione um arquivo'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await fetch(`${API}/tarefas/importar/upload`, { method: 'POST', body: fd });
      if (!upRes.ok) {
        const e = await upRes.json().catch(() => ({}));
        throw new Error(e?.detail || 'Erro no upload');
      }
      const up = await upRes.json();

      const prRes = await fetch(`${API}/tarefas/importar/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: up.upload_id }),
      });
      if (!prRes.ok) {
        const e = await prRes.json().catch(() => ({}));
        throw new Error(e?.detail || 'Erro no preview');
      }
      const pr = await prRes.json();
      setPreviewData(pr);
      setStep(STEP.PREVIEW);
    } catch (e) {
      setError(e.message || 'Erro no processamento');
    } finally {
      setUploading(false);
    }
  };

  const handleExecute = async () => {
    if (!previewData?.upload_id) return;
    setExecuting(true); setError('');
    try {
      const res = await fetch(`${API}/tarefas/importar/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id: previewData.upload_id }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.detail || 'Erro na execucao');
      }
      const r = await res.json();
      setResult(r);
      setStep(STEP.RESULT);
    } catch (e) {
      setError(e.message || 'Erro na execucao');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="tarefas-importar-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <FileArrowUp size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Importar Tarefas</h2>
              <p className="text-xs text-[#71717A]">Arquivo gerado pelo Caixa30 V2 &middot; aba &quot;Importacao SalonUP&quot;</p>
            </div>
          </div>
          <button data-testid="btn-close-import" onClick={handleClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2" data-testid="import-error">
              <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === STEP.UPLOAD && (
            <UploadStep
              file={file}
              onChoose={handleFileChoose}
              onUpload={handleUpload}
              uploading={uploading}
              fileInputRef={fileInputRef}
            />
          )}

          {step === STEP.PREVIEW && previewData && (
            <PreviewStep data={previewData} />
          )}

          {step === STEP.RESULT && result && (
            <ResultStep result={result} />
          )}
        </div>

        {/* Footer */}
        {step === STEP.PREVIEW && previewData && (
          <div className="border-t border-[#E4E4E7] p-4 flex items-center justify-between gap-3 bg-[#FAFAF9]">
            <button
              data-testid="btn-voltar-upload"
              onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-[#71717A] hover:bg-white border border-[#E4E4E7]"
            >
              <ArrowClockwise size={14} weight="bold" /> Enviar outro arquivo
            </button>
            <button
              data-testid="btn-confirmar-importacao"
              onClick={handleExecute}
              disabled={executing || previewData.stats.validas === 0}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#1B4332' }}
            >
              {executing
                ? <><Spinner size={14} className="animate-spin" /> Importando...</>
                : <><Check size={14} weight="bold" /> Confirmar importacao ({previewData.stats.validas})</>}
            </button>
          </div>
        )}

        {step === STEP.RESULT && (
          <div className="border-t border-[#E4E4E7] p-4 flex items-center justify-end gap-2 bg-[#FAFAF9]">
            <button
              data-testid="btn-nova-importacao"
              onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-[#71717A] hover:bg-white border border-[#E4E4E7]"
            >
              <ArrowClockwise size={14} weight="bold" /> Nova importacao
            </button>
            <button
              data-testid="btn-fechar-resultado"
              onClick={handleClose}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: '#1B4332' }}
            >
              Concluir
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== UPLOAD STEP ====================
const UploadStep = ({ file, onChoose, onUpload, uploading, fileInputRef }) => (
  <div className="space-y-4">
    <div className="rounded-2xl border-2 border-dashed border-[#E4E4E7] p-8 text-center bg-[#FAFAF9]"
      data-testid="upload-dropzone">
      <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
        style={{ backgroundColor: '#1B433215' }}>
        <CloudArrowUp size={28} weight="regular" style={{ color: '#1B4332' }} />
      </div>
      <p className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
        Envie o arquivo do Caixa30 V2
      </p>
      <p className="text-xs text-[#71717A] mt-1">
        Formatos aceitos: .xlsx, .csv &middot; Max: 25 MB
      </p>
      <p className="text-[10px] text-[#A1A1AA] mt-1">
        Para Excel, deve conter a aba <b>Importacao SalonUP</b>
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onChoose}
        className="hidden"
        data-testid="input-file"
      />
      <button
        data-testid="btn-choose-file"
        onClick={() => fileInputRef.current?.click()}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
        style={{ backgroundColor: '#1B4332' }}
      >
        <UploadSimple size={14} weight="bold" /> Selecionar arquivo
      </button>
      {file && (
        <p className="mt-3 text-xs text-[#18181B]" data-testid="selected-file">
          Selecionado: <b>{file.name}</b> ({(file.size / 1024).toFixed(1)} KB)
        </p>
      )}
    </div>

    <div className="rounded-xl bg-[#F7F6F4] border border-[#E4E4E7] p-4 text-xs text-[#71717A] leading-relaxed">
      <p className="font-semibold text-[#18181B] mb-1">Colunas esperadas</p>
      <p><code className="text-[10px]">id_origem, titulo, descricao, data, horario, prioridade, categoria, responsavel</code></p>
      <p className="mt-2">
        Campos obrigatorios: <b>id_origem</b>, <b>titulo</b>, <b>data</b>. Prioridades: baixa, normal, urgente. Categorias: financeiro, cliente, equipe, fornecedor, operacao, pessoal.
      </p>
    </div>

    <div className="flex justify-end">
      <button
        data-testid="btn-enviar-arquivo"
        onClick={onUpload}
        disabled={!file || uploading}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#1B4332' }}
      >
        {uploading
          ? <><Spinner size={14} className="animate-spin" /> Analisando...</>
          : <>Analisar arquivo</>}
      </button>
    </div>
  </div>
);

// ==================== PREVIEW STEP ====================
const PreviewStep = ({ data }) => {
  const { filename, total_rows, stats, linhas } = data;
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[#F7F6F4] border border-[#E4E4E7] p-3 flex items-center gap-2 text-xs">
        <span className="text-[#71717A]">Arquivo:</span>
        <b className="text-[#18181B]" data-testid="preview-filename">{filename}</b>
        <span className="text-[#A1A1AA] mx-2">&middot;</span>
        <span className="text-[#71717A]">Total de linhas:</span>
        <b className="text-[#18181B]" data-testid="preview-total">{total_rows}</b>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Validas" value={stats.validas} color="#1B4332" testid="stat-validas" />
        <Stat label="Com erro" value={stats.erros} color="#B91C1C" testid="stat-erros" />
        <Stat label="Duplic. confirmadas" value={stats.duplicadas_confirmadas} color="#B45309" testid="stat-dup-conf" />
        <Stat label="Duplic. possiveis" value={stats.duplicadas_possiveis} color="#0369A1" testid="stat-dup-poss" />
      </div>

      <div className="rounded-xl border border-[#E4E4E7] overflow-hidden">
        <div className="px-3 py-2 bg-[#FAFAF9] border-b border-[#E4E4E7]">
          <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wide">Detalhamento das linhas</p>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-[#F4F4F5]" data-testid="preview-linhas">
          {linhas.map((l) => <LinePreview key={l.linha} l={l} />)}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div className="salonup-card p-3" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>{label}</p>
    <p className="text-xl font-semibold mt-0.5" style={{ fontFamily: 'Outfit', color }}>{value}</p>
  </div>
);

const LinePreview = ({ l }) => {
  const { linha, record, errors, ajustes, dup_confirmada, dup_possivel, valida } = l;
  const state = dup_confirmada ? 'dup' : (errors.length > 0 ? 'err' : (dup_possivel ? 'warn' : 'ok'));
  const badge = {
    ok: { label: 'Valida', bg: '#DCFCE7', color: '#1B4332', Ic: CheckCircle },
    err: { label: 'Erro', bg: '#FEE2E2', color: '#B91C1C', Ic: XCircle },
    dup: { label: 'Duplicada', bg: '#FEF3C7', color: '#B45309', Ic: Warning },
    warn: { label: 'Possivel dup.', bg: '#DBEAFE', color: '#0369A1', Ic: Warning },
  }[state];
  const BIc = badge.Ic;
  return (
    <div className="px-3 py-2.5 text-xs" data-testid={`preview-line-${linha}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[#A1A1AA] w-10">L{linha}</span>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
          style={{ backgroundColor: badge.bg, color: badge.color }}>
          <BIc size={10} weight="fill" /> {badge.label}
        </span>
        <span className="flex-1 truncate text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
          {record.titulo || <i className="text-[#A1A1AA]">(sem titulo)</i>}
        </span>
        <span className="text-[10px] text-[#71717A]">{record.data || '—'} {record.horario}</span>
      </div>
      {(record.prioridade || record.categoria || record.responsavel) && (
        <p className="text-[10px] text-[#A1A1AA] ml-12 mt-0.5">
          {record.prioridade && <>Prio: <b>{record.prioridade}</b> · </>}
          {record.categoria && <>Cat: <b>{record.categoria}</b> · </>}
          {record.responsavel && <>Resp: <b>{record.responsavel}</b></>}
        </p>
      )}
      {ajustes.length > 0 && errors.length === 0 && (
        <ul className="ml-12 mt-1 space-y-0.5">
          {ajustes.map((a, idx) => (
            <li key={idx} className="text-[10px] text-[#0369A1]">
              Default aplicado em <b>{a.campo}</b>: {a.aplicado}
            </li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <ul className="ml-12 mt-1 space-y-0.5">
          {errors.map((e, idx) => (
            <li key={idx} className="text-[10px] text-[#B91C1C]">
              <b>{e.campo}</b>: {e.motivo}{e.valor ? ` (recebido: "${e.valor}")` : ''}. {e.correcao}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ==================== RESULT STEP ====================
const ResultStep = ({ result }) => (
  <div className="space-y-4">
    <div className="rounded-2xl border border-[#E4E4E7] bg-[#F0FDF4] p-6 text-center" data-testid="result-summary">
      <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3"
        style={{ backgroundColor: '#1B433215' }}>
        <CheckCircle size={28} weight="fill" style={{ color: '#1B4332' }} />
      </div>
      <p className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
        Importacao concluida
      </p>
      <p className="text-xs text-[#71717A] mt-1">Resumo do processamento</p>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Processado" value={result.processed} color="#71717A" testid="result-processed" />
      <Stat label="Importado" value={result.imported} color="#1B4332" testid="result-imported" />
      <Stat label="Ignorado (dup.)" value={result.skipped} color="#B45309" testid="result-skipped" />
      <Stat label="Com erro" value={result.errors} color="#B91C1C" testid="result-errors" />
    </div>
  </div>
);
